import { AtSign, KeyRound, LockKeyhole, LogIn, Mail, Sprout, UserPlus } from "lucide-react";
import { useState } from "react";
import { forgotPassword } from "../lib/api";
import { LanguageToggle } from "./LanguageToggle";

type Props = {
  onLoginCredentials: (input: { email: string; password: string }) => Promise<void>;
  onRegister: (input: { email: string; username: string; password: string }) => Promise<void>;
  onLoginPasscode: (passcode: string) => Promise<void>;
  error?: string;
};

type Mode = "login" | "register" | "forgot";

export function LoginScreen({ onLoginCredentials, onRegister, onLoginPasscode, error }: Props) {
  const showOwnerPasscodeFallback =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    new URLSearchParams(window.location.search).get("owner") === "1";
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPasscode, setShowPasscode] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotLink, setForgotLink] = useState("");

  const submitForgot = async () => {
    if (!email.trim()) {
      return;
    }
    setIsSubmitting(true);
    setForgotMsg("");
    setForgotLink("");
    try {
      const res = await forgotPassword(email.trim());
      setForgotMsg("Nếu email tồn tại, mình đã gửi link đặt lại mật khẩu. Kiểm tra hộp thư nhé.");
      if (res.devResetUrl) {
        setForgotLink(res.devResetUrl);
      }
    } catch {
      setForgotMsg("Có lỗi khi gửi yêu cầu. Thử lại sau.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    mode === "login"
      ? email.trim().length > 0 && password.length > 0
      : email.trim().length > 0 && username.trim().length >= 2 && password.length >= 6;

  const submit = async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    try {
      if (mode === "login") {
        await onLoginCredentials({ email: email.trim(), password });
      } else {
        await onRegister({ email: email.trim(), username: username.trim(), password });
      }
      setPassword("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitPasscode = async () => {
    if (!passcode.trim()) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onLoginPasscode(passcode.trim());
      setPasscode("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper px-4 py-10 text-ink">
      <section className="w-full max-w-md rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
                <Sprout size={21} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold">Human Note</h1>
                <p className="text-sm font-medium text-muted">
                  {mode === "forgot"
                    ? "Đặt lại mật khẩu qua email"
                    : mode === "login"
                      ? "Đăng nhập vào khu vườn của bạn"
                      : "Tạo khu vườn riêng của bạn"}
                </p>
              </div>
            </div>
            <LanguageToggle />
          </div>
        </div>

        {/* mode tabs */}
        {mode !== "forgot" ? (
          <div className="flex gap-1 border-b border-line p-2">
            <button
              type="button"
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold ${mode === "login" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setMode("login")}
            >
              Đăng nhập
            </button>
            <button
              type="button"
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold ${mode === "register" ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setMode("register")}
            >
              Đăng ký
            </button>
          </div>
        ) : null}

        {mode === "forgot" ? (
          <div className="space-y-3 p-5">
            <Field icon={<Mail size={17} aria-hidden="true" />} label="Email của bạn">
              <input
                className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void submitForgot();
                  }
                }}
                placeholder="ban@email.com"
                autoComplete="email"
              />
            </Field>
            <button
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-300"
              type="button"
              disabled={!email.trim() || isSubmitting}
              onClick={submitForgot}
            >
              <Mail size={17} aria-hidden="true" /> {isSubmitting ? "Đang gửi" : "Gửi link đặt lại"}
            </button>
            {forgotMsg ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">{forgotMsg}</p> : null}
            {forgotLink ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                (Local test) Link đặt lại: <a className="font-bold underline" href={forgotLink}>{forgotLink}</a>
              </p>
            ) : null}
            <button type="button" className="text-sm font-semibold text-muted hover:text-brand" onClick={() => setMode("login")}>
              ← Về đăng nhập
            </button>
          </div>
        ) : (
        <div className="space-y-3 p-5">
          <Field icon={<AtSign size={17} aria-hidden="true" />} label="Email">
            <input
              className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="ban@email.com"
              autoComplete="email"
            />
          </Field>

          {mode === "register" ? (
            <Field icon={<UserPlus size={17} aria-hidden="true" />} label="Tên người dùng">
              <input
                className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="ten_cua_ban"
                autoComplete="username"
              />
            </Field>
          ) : null}

          <Field icon={<LockKeyhole size={17} aria-hidden="true" />} label={mode === "register" ? "Mật khẩu (ít nhất 6 ký tự)" : "Mật khẩu"}>
            <input
              className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void submit();
                }
              }}
              placeholder="••••••••"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
            />
          </Field>

          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p>
          ) : null}

          <button
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            type="button"
            disabled={!canSubmit || isSubmitting}
            onClick={submit}
          >
            {mode === "login" ? <LogIn size={17} aria-hidden="true" /> : <UserPlus size={17} aria-hidden="true" />}
            {isSubmitting ? "Đang xử lý" : mode === "login" ? "Đăng nhập" : "Tạo tài khoản"}
          </button>

          {mode === "login" ? (
            <button type="button" className="text-sm font-semibold text-muted hover:text-brand" onClick={() => setMode("forgot")}>
              Quên mật khẩu?
            </button>
          ) : null}

          {/* owner passcode fallback */}
          {showOwnerPasscodeFallback ? (
          <div className="border-t border-line pt-3">
            {showPasscode ? (
              <div className="space-y-2">
                <div className="flex h-11 items-center rounded-lg border border-line bg-slate-50 px-3 text-muted">
                  <KeyRound size={17} aria-hidden="true" />
                  <input
                    className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm font-semibold text-ink outline-none placeholder:text-slate-400"
                    type="password"
                    value={passcode}
                    onChange={(event) => setPasscode(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        void submitPasscode();
                      }
                    }}
                    placeholder="Mã owner"
                    autoComplete="off"
                  />
                </div>
                <button
                  className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-white px-3 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:bg-slate-100"
                  type="button"
                  disabled={!passcode.trim() || isSubmitting}
                  onClick={submitPasscode}
                >
                  Đăng nhập bằng mã owner
                </button>
              </div>
            ) : (
              <button type="button" className="text-xs font-semibold text-muted hover:text-brand" onClick={() => setShowPasscode(true)}>
                Đăng nhập owner bằng mã (cũ)
              </button>
            )}
          </div>
          ) : null}
        </div>
        )}
      </section>
    </main>
  );
}

function Field({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold">{label}</label>
      <div className="flex h-11 items-center rounded-lg border border-line bg-slate-50 px-3 text-muted">
        {icon}
        {children}
      </div>
    </div>
  );
}
